/** Canonical permission codes (aligned with backend agency/constants.py). Import these instead of repeating strings. */
export const PERMISSION_CODES = Object.freeze({
  USER_MANAGE: "user.manage",
  USER_LIST: "user.list",
  AGENCY_MANAGE: "agency.manage",
  AD_REQUEST_VIEW: "ad_request.view",
  AD_REQUEST_CREATE: "ad_request.create",
  AD_REQUEST_UPDATE: "ad_request.update",
  AD_DELIVER_VIEW: "ad_deliver.view",
  AD_DELIVER_CREATE: "ad_deliver.create",
  AD_QC_REVIEW: "ad_qc.review",
  AD_MODERATION_VIEW: "ad_moderation.view",
  AD_MODERATION_REVIEW: "ad_moderation.review",
  AD_MODERATION_ENTER_ERRORS: "ad_moderation.enter_errors",
  AD_LAUNCH_VIEW: "ad_launch.view",
  AD_LAUNCH_CREATE: "ad_launch.create",
  AD_LAUNCH_SET_LIVE: "ad_launch.set_live",
  RATE_CARD_VIEW: "rate_card.view",
  RATE_CARD_MANAGE: "rate_card.manage",
  CATEGORY_TRACK_MAPPING_LIST: "category_track.list",
  CATEGORY_TRACK_MAPPING_VIEW: "category_track.view",
  CATEGORY_TRACK_MAPPING_MANAGE: "category_track.manage",
  TAXONOMY_REVIEW: "taxonomy.review",
});

/**
 * Permissions that grant visibility to the /agency list API.
 * Mirrors agency/api/agency_v1.py permission gate. AGENCY_MANAGE sees full
 * fields; AD_REQUEST_CREATE sees minimal fields (id + name).
 */
export const AGENCY_LIST_PERMISSIONS = [
  PERMISSION_CODES.AGENCY_MANAGE,
  PERMISSION_CODES.AD_REQUEST_CREATE,
];

const P = PERMISSION_CODES;

export const ROUTE_PERMISSIONS = {
  "/dashboard/ads": [P.AD_REQUEST_VIEW],
  "/dashboard/ad-upload": [P.AD_DELIVER_VIEW, P.AD_DELIVER_CREATE],
  "/dashboard/ad-request": [
    P.AD_REQUEST_VIEW,
    P.AD_REQUEST_CREATE,
    P.AD_REQUEST_UPDATE,
  ],
  "/dashboard/ad-request/assigned": [
    P.AD_REQUEST_VIEW,
    P.AD_REQUEST_CREATE,
    P.AD_REQUEST_UPDATE,
  ],
  "/dashboard/agency_sprints": [P.AD_REQUEST_VIEW, P.AD_REQUEST_UPDATE],
  "/dashboard/ad-moderation": [P.AD_MODERATION_VIEW, P.AD_MODERATION_REVIEW],
  "/dashboard/ad-launch": [P.AD_LAUNCH_VIEW, P.AD_LAUNCH_CREATE],
  "/dashboard/agency-user-roles": [P.USER_MANAGE, P.AGENCY_MANAGE],
  "/dashboard/ad-rate-card": [P.RATE_CARD_VIEW, P.RATE_CARD_MANAGE],
  "/dashboard/ad-recco": [P.RATE_CARD_VIEW, P.RATE_CARD_MANAGE],
  "/dashboard/category-track-mapping": [
    P.CATEGORY_TRACK_MAPPING_VIEW,
    P.CATEGORY_TRACK_MAPPING_MANAGE,
  ],
  "/dashboard/ad-intelligence": [P.TAXONOMY_REVIEW],
};

export const PERMISSION_TO_ROUTES = Object.entries(ROUTE_PERMISSIONS).reduce(
  (acc, [path, codes]) => {
    codes.forEach((code) => {
      if (!acc[code]) acc[code] = [];
      if (!acc[code].includes(path)) acc[code].push(path);
    });
    return acc;
  },
  {}
);

export const getRequiredPermissionsForRoute = (path) =>
  ROUTE_PERMISSIONS[path] ?? [];

/** Seekho staff email domain; matches SEEKHO_EMAIL_DOMAIN in the backend's agency/constants.py. */
export const SEEKHO_EMAIL_DOMAIN = "seekhoapp.com";

/** Routes only Seekho email accounts may open, on top of their permission codes; super_admin included. */
export const SEEKHO_EMAIL_ROUTES = ["/dashboard/ad-intelligence"];

/** Whether the address is on the Seekho staff domain, matched exactly after the @. */
export const handleIsSeekhoEmail = ({ email }) => {
  const value = (email || "").trim().toLowerCase();
  const at = value.lastIndexOf("@");
  return at > 0 && value.slice(at + 1) === SEEKHO_EMAIL_DOMAIN;
};

/** Per sidebar link / deep link — OR semantics: user needs at least one listed permission. */
export const NAV_LINK_PERMISSIONS = {
  "/dashboard/ads": [P.AD_REQUEST_VIEW],
  "/dashboard/ad-request?open=create": [P.AD_REQUEST_CREATE],
  "/dashboard/ad-request": [P.AD_REQUEST_VIEW],
  "/dashboard/ad-request/assigned": [P.AD_REQUEST_VIEW],
  "/dashboard/agency_sprints?open=sprint": [P.AD_REQUEST_UPDATE],
  "/dashboard/ad-upload?open=delivery": [P.AD_DELIVER_CREATE],
  "/dashboard/ad-upload": [P.AD_DELIVER_VIEW],
  "/dashboard/ad-upload?rejected=1": [P.AD_DELIVER_VIEW],
  "/dashboard/agency_sprints": [P.AD_REQUEST_VIEW],
  "/dashboard/ad-moderation": [P.AD_MODERATION_VIEW, P.AD_MODERATION_REVIEW],
  "/dashboard/ad-launch": [P.AD_LAUNCH_VIEW, P.AD_LAUNCH_CREATE],
  "/dashboard/ad-rate-card": [P.RATE_CARD_VIEW, P.RATE_CARD_MANAGE],
  "/dashboard/ad-recco": [P.RATE_CARD_VIEW, P.RATE_CARD_MANAGE],
  "/dashboard/category-track-mapping": [
    P.CATEGORY_TRACK_MAPPING_VIEW,
    P.CATEGORY_TRACK_MAPPING_MANAGE,
  ],
  "/dashboard/ad-intelligence": [P.TAXONOMY_REVIEW],
  "/dashboard/agency-user-roles?tab=agencies": [P.AGENCY_MANAGE],
  "/dashboard/agency-user-roles?tab=users": [P.USER_MANAGE],
  "/dashboard/agency-user-roles?tab=roles": [P.USER_MANAGE],
  "/dashboard/agency-user-roles?tab=permissions": [P.USER_MANAGE],
  "/dashboard/agency-user-roles": [P.USER_MANAGE, P.AGENCY_MANAGE],
};

/**
 * Permissions required to show a specific nav link (pathname + query).
 * Falls back to route-level OR list for paths not in NAV_LINK_PERMISSIONS.
 */
export const getRequiredPermissionsForNavLink = (to) => {
  if (!to || typeof to !== "string") return [];
  const normalized = to.split("#")[0];
  if (Object.prototype.hasOwnProperty.call(NAV_LINK_PERMISSIONS, normalized)) {
    return NAV_LINK_PERMISSIONS[normalized];
  }
  const pathOnly = normalized.split("?")[0];
  if (Object.prototype.hasOwnProperty.call(NAV_LINK_PERMISSIONS, pathOnly)) {
    return NAV_LINK_PERMISSIONS[pathOnly];
  }
  return getRequiredPermissionsForRoute(pathOnly);
};

export const DASHBOARD_ROUTE_ORDER = [
  "/dashboard/ads",
  "/dashboard/ad-request",
  "/dashboard/ad-upload",
  "/dashboard/agency_sprints",
  "/dashboard/ad-moderation",
  "/dashboard/ad-launch",
  "/dashboard/agency-user-roles",
  "/dashboard/ad-rate-card",
  "/dashboard/ad-recco",
  "/dashboard/category-track-mapping",
  "/dashboard/ad-intelligence",
];

export const getFirstAllowedDashboardPath = (hasRouteAccess) => {
  const first = DASHBOARD_ROUTE_ORDER.find(hasRouteAccess);
  return first ?? "/profile";
};
